import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,Button,
  Paper, IconButton, InputBase, Pagination, Menu, MenuItem,CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExportDropdown from "../../../components/ExportDropdown";
import CustomText, { textStyles } from "../../../components/CustomText";
import StatusDropdown from "../../../components/StatusDropdown";
import RequestDetailsDrawer from "./DetailsDrawer";
import { useTranslation } from "react-i18next";
import { showUnderDevelopment } from "../../../components/alerts.js";
import { decryptToken } from "../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../components/BaseUrl.js";
import TruncatedCell from "../../../components/TruncatedCell.js";
import FilterDrawer from "./filterDrawer.js";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import emptypayment from "../../../assets/emptypayment.png";
import AddIcon from "@mui/icons-material/Add";
import GenerateRequestDrawer from "./AddDrawer.js";
import {useLocation,useNavigate} from "react-router-dom";
import back from "../../../assets/back.svg";
import ExportRequestsExcel from "./RequestExcel.js";
import ExportRequestsPDF from "./RequestPdf.js";
import { pdf } from "@react-pdf/renderer";
import CustomButton from "../../../components/CustomButton.js";
import CancelRequestModal from "./CancelRequest.js";
import {toast,Toaster} from "react-hot-toast";
import {useCompanyLogo} from "../../../components/FetchLogoPdf"
import { convertUTCToLocalDateTime } from "../../../components/dateTimeUtils";
const HeaderStyles = {
  color: "#44546F",
  fontSize: "15px",
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  whiteSpace: "nowrap",
};

const DataStyles = {
  color: "#172B4D",
  fontSize: "13px",
  fontWeight: 400,
  fontFamily: "Poppins, sans-serif",
  whiteSpace: "nowrap",
};

const RequestManagement = () => {
  const { t } = useTranslation();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
const [refreshList, setRefreshList] = useState(false);
const location = useLocation();
  const navigate = useNavigate();
  const fromDashboard = location.state?.fromDashboard;

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
const [initialLoading, setInitialLoading] = useState(true);   // full screen loader
const [tableLoading, setTableLoading] = useState(false); 
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
const [filterOpen, setFilterOpen] = useState(false);
const [filters, setFilters] = useState({ status: "", startDate: "", endDate: "" });
const [addDrawerOpen, setAddDrawerOpen] = useState(false);
/** Rows per page — sent as `limit` (must match what you want from the API). */
const PAGE_LIMIT = 5;
const [totalServerPages, setTotalServerPages] = useState(1);

const parseRequestsPayload = (data) => {
  const root = data?.data;
  let list = [];
  let pagination = data?.pagination;

  if (Array.isArray(root)) {
    list = root;
  } else if (root && typeof root === "object") {
    pagination = root.pagination ?? pagination;
    if (Array.isArray(root.requests)) list = root.requests;
    else if (Array.isArray(root.data)) list = root.data;
    else if (Array.isArray(root.items)) list = root.items;
  }

  return { list, pagination };
};

const fetchRequests = async (
  isInitial = false,
  appliedFilters = filters,
  pageNum = 1
) => {
  try {
    if (isInitial) setInitialLoading(true);
    else setTableLoading(true);

    const { status, startDate, endDate } = appliedFilters;
    const params = new URLSearchParams();
    params.set("page", String(pageNum));
    params.set("limit", String(PAGE_LIMIT));
    if (status) params.append("status", status);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const url = `${BASE_URL}/requests/v1/requests/my?${params.toString()}`;

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const data = await res.json();

    const { list, pagination } = parseRequestsPayload(data);
    setRequests(list);

    const total = pagination?.total ?? 0;
    const pages =
      pagination?.pages ??
      (total > 0 ? Math.ceil(total / PAGE_LIMIT) : 1);
    setTotalServerPages(Math.max(1, pages));
  } catch (err) {
    console.error("Failed to fetch requests", err);
  } finally {
    setInitialLoading(false);
    setTableLoading(false);
  }
};

/**
 * When searching, fetch ALL matching requests (no_pagination) instead of one
 * server page, then paginate on the client. Needed because a match can fall
 * on any server page — searching from page 2 must not miss matches that live
 * on page 1, 3, etc.
 */
const fetchSearchResults = async (
  isInitial = false,
  appliedFilters = filters,
  pageNum = 1,
  searchTerm = search
) => {
  try {
    if (isInitial) setInitialLoading(true);
    else setTableLoading(true);

    let url = `${BASE_URL}/requests/v1/requests/my?no_pagination=true`;
    if (appliedFilters.status) url += `&status=${appliedFilters.status}`;
    if (appliedFilters.startDate) url += `&start_date=${appliedFilters.startDate}`;
    if (appliedFilters.endDate) url += `&end_date=${appliedFilters.endDate}`;

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const data = await res.json();
    const { list } = parseRequestsPayload(data);

    const q = searchTerm.toLowerCase().trim();
    const matched = q
      ? list.filter((row) => {
          const typeLabel = formatRequestTypeLabel(row.type).toLowerCase();
          return (
            row.type?.toLowerCase().includes(q) ||
            typeLabel.includes(q) ||
            row.leave_reason_text?.toLowerCase().includes(q) ||
            row.status?.toLowerCase().includes(q)
          );
        })
      : list;

    const pages = Math.max(1, Math.ceil(matched.length / PAGE_LIMIT));
    const safePage = Math.min(pageNum, pages);
    const start = (safePage - 1) * PAGE_LIMIT;
    setRequests(matched.slice(start, start + PAGE_LIMIT));
    setTotalServerPages(pages);
    if (safePage !== pageNum) setCurrentPage(safePage);
  } catch (err) {
    console.error("Failed to fetch requests", err);
  } finally {
    setInitialLoading(false);
    setTableLoading(false);
  }
};

const isFirstFetch = useRef(true);
useEffect(() => {
  const isInitial = isFirstFetch.current;
  isFirstFetch.current = false;
  if (search.trim()) {
    fetchSearchResults(isInitial, filters, currentPage, search);
  } else {
    fetchRequests(isInitial, filters, currentPage);
  }
}, [
  currentPage,
  filters.status,
  filters.startDate,
  filters.endDate,
  refreshList,
  search,
]);
const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const localDateTime = convertUTCToLocalDateTime(dateString, { locale: "en-GB" });
  return localDateTime === "—" ? "-" : localDateTime;
};

const formatDateOnly = (dateString) => {
  if (!dateString) return "-";
  const localDate = convertUTCToLocalDateTime(dateString, {
    locale: "en-GB",
    formatOptions: {
      day: "2-digit",
      month: "short",
      year: "numeric",
    },
  });
  return localDate === "—" ? "-" : localDate;
};

const formatTimeOnly = (timeString) => {
  if (!timeString) return "-";
  const localTime = convertUTCToLocalDateTime(timeString, {
    locale: "en-GB",
    formatOptions: {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    },
  });
  return localTime === "—" ? "-" : localTime;
};

/** API stores ENUM-style values; table shows title labels (e.g. SCHEDULE_CHANGE → Schedule Change). */
const formatRequestTypeLabel = (type) => {
  if (!type) return "-";
  const keyByType = {
    LEAVE: "options.leave",
    SCHEDULE_CHANGE: "options.scheduleChange",
    GENERIC_HR: "options.genericHR",
  };
  const i18nKey = keyByType[type];
  if (i18nKey) return t(i18nKey);
  return String(type)
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
};

/** Leave reason code enum → translated label. */
const formatReasonCodeLabel = (code) => {
  if (!code) return "-";
  const keyByCode = {
    URGENT: "options.leaveReasonCodes.urgent",
    SICK: "options.leaveReasonCodes.sick",
    VACATION: "options.leaveReasonCodes.vacation",
    OTHER: "options.leaveReasonCodes.others",
  };
  const i18nKey = keyByCode[code];
  return i18nKey ? t(i18nKey) : code;
};

const fetchAllRequests = async (token, filters = {}) => {
  try {
   let url = `${BASE_URL}/requests/v1/requests/my?no_pagination=true`;

    if (filters.status) url += `&status=${filters.status}`;
    if (filters.startDate) url += `&start_date=${filters.startDate}`;
    if (filters.endDate) url += `&end_date=${filters.endDate}`;

    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const json = await res.json();

    if (json.error) {
      console.error("Failed to fetch requests for export:", json.message);
      return [];
    }

    const { list } = parseRequestsPayload(json);

    return list.map((item) => {
      let reasonDetails = "-";
      if (item.type === "LEAVE") {
        reasonDetails = item.leave_reason_text || "-";
      } else if (item.type === "SCHEDULE_CHANGE") {
        reasonDetails = item.sched_reason_text || "-";
      } else if (item.type === "GENERIC_HR") {
        reasonDetails = item.details || "-";
      }

      return {
        id: item.id,
        type: formatRequestTypeLabel(item.type),
        reasonCode: item.type === "LEAVE" ? formatReasonCodeLabel(item.leave_reason_code) : "-",
        reason: reasonDetails,
        date: formatDateTime(item.created_at),
        comments: item.admin_comment || "-",
        status: item.status || "-",
      };
    });
  } catch (err) {
    console.error("Error fetching all requests for export:", err);
    return [];
  }
};
const { logo } = useCompanyLogo(token);
const filteredRequests = useMemo(() => {
  const lower = search.toLowerCase().trim();
  if (!lower) return requests;
  return requests.filter((row) => {
    const typeLabel = formatRequestTypeLabel(row.type).toLowerCase();
    return (
      row.type?.toLowerCase().includes(lower) ||
      typeLabel.includes(lower) ||
      row.leave_reason_text?.toLowerCase().includes(lower) ||
      row.status?.toLowerCase().includes(lower)
    );
  });
}, [search, requests, t]);

useEffect(() => {
  setCurrentPage(1);
}, [search]);
const [cancelModalOpen, setCancelModalOpen] = useState(false);
const [cancelLoading, setCancelLoading] = useState(false);

// 👇 New for resubmit
const [resubmitModalOpen, setResubmitModalOpen] = useState(false);
const [resubmitLoading, setResubmitLoading] = useState(false);
const [uploadedFiles, setUploadedFiles] = useState([]); 
const handleCancelRequest = async (requestId) => {
  if (!requestId) return;
  try {
    setCancelLoading(true);

    const res = await fetch(`${BASE_URL}/requests/v1/requests/${requestId}/cancel`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();

    if (!res.ok) throw new Error(data.message || "Cancel failed");

    setCancelModalOpen(false);
    setRefreshList((prev) => !prev); // refresh list
  } catch (err) {
    console.error("❌ Cancel Request Error:", err);
  } finally {
    setCancelLoading(false);
  }
};

const handleResubmitRequest = async (requestId, data) => {
  try {
    setResubmitLoading(true);
    const res = await fetch(`${BASE_URL}/requests/v1/requests/${requestId}/resubmit`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const json = await res.json();
    if (!res.ok) throw new Error(json.message || "Resubmit failed");

    toast.success("Request resubmitted successfully");
    setResubmitModalOpen(false);
    setRefreshList((prev) => !prev);
  } catch (err) {
    console.error("❌ Resubmit error:", err);
    toast.error(err.message || "Resubmit failed");
  } finally {
    setResubmitLoading(false);
  }
};


  return (
    <>
                   {fromDashboard && (
      <Box
        sx={{
          border: '1px solid #E4E6EF',
          borderRadius: 2,
          px: 2,
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minHeight: 55,
          mb: 2,
          ml:1, mr:1
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
          onClick={() => navigate(-1)}
        >
          <img src={back} style={{ width: 30, height: 30 }} />
          <CustomText
            sx={{
              ...textStyles.h1,
              fontWeight: 500,
              fontSize: '14px',
              color: '#626F86',
              textAlign: 'center'
            }}
          >
            {t('worker')} 
          </CustomText>
        </Box>
      </Box>
    )}
    <Box sx={{ backgroundColor: "white", borderRadius: 2, border: "2px solid #091E4224", py: 2, px: 2, mt: 1, ml:1, mr:1 }}>
      
      {/* Header */}
      <Box
        sx={{
          pb: 2,
          borderBottom: "2px solid #091E4224",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  <CustomText
    sx={{ ...textStyles.h1, fontWeight: 600, fontSize: "18px", color: "#003149" }}
  >
    {t("requestManagement.heading")}
  </CustomText>
  {tableLoading && !initialLoading && (
    <CircularProgress size={20} />
  )}</Box>
        {/* Search */}
        <Box
          sx={{
            px: 1,
            py: 0.2,
            borderRadius: 1,
            height: 34,
            border: "2px solid #091E4224",
            display: "flex",
            alignItems: "center",
            minWidth: 250,
            maxWidth: 300,
          }}
        >
          <InputBase
            placeholder={t("search_request_by_type")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              fontSize: 14,
              fontFamily: "Poppins, sans-serif",
              color: "#939393",
              width: "100%",
            }}
          />
          <SearchIcon sx={{ fontSize: 20, color: "#888", ml: 1 }} />
        </Box>

        {/* Export & Filter */}
        <Box sx={{ display: "flex", flexDirection:"row",alignItems: "center", gap: 1 }}> 
         <ExportDropdown
  onDownloadPDF={async () => {
    const allData = await fetchAllRequests(token, filters);
    const blobStream = pdf(<ExportRequestsPDF data={allData}  logo={logo} />).toBlob();
    blobStream.then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Requests_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
    });
  }}
  onDownloadExcel={async () => {
    const allData = await fetchAllRequests(token, filters);
    ExportRequestsExcel(allData);
  }}
/>

                      <CustomButton
  onClick={() => setAddDrawerOpen(true)}
  label={t("common.add") || "Add"}
  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
  variant="contained"
  size="small"
  sx={{
    px: 2,
    // backgroundColor: "#006EC2",
    height: 32,
    fontSize: 14,
    fontWeight: 400,
    fontFamily: "Poppins, sans-serif",
    borderRadius: "4px",
    textTransform: "none",
    // color: "white",
  }}
/>
                    
 {filters.status || filters.startDate || filters.endDate ? (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 34,
        borderRadius: 1,
        bgcolor: "#1976d2", // blue background
        cursor: "pointer",
      }}
      onClick={() => setFilterOpen(true)} // open drawer when clicking main icon
    >
      {/* Filter Icon (white) */}
      <FilterAltIcon sx={{ color: "white", fontSize: 20 }} />

      {/* Small Clear Button */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation(); // prevent drawer from opening
          const cleared = { status: "", startDate: "", endDate: "" };
          setFilters(cleared);
          setCurrentPage(1);
        }}
        sx={{
          position: "absolute",
          top: -6,
          right: -6,
          bgcolor: "white",
          width: 18,
          height: 18,
          border: "1px solid #C4C4C4",
          "&:hover": { bgcolor: "#f5f5f5" },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 14, color: "#333" }} />
      </IconButton>
    </Box>
  ) : (
    <IconButton
      onClick={() => setFilterOpen(true)}
      sx={{
        border: "1px solid #C4C4C4",
        borderRadius: 1,
        width: 34,
        height: 34,
      }}
    >
      <FilterAltIcon />
    </IconButton>
  )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.id")}</TableCell>
              {/* <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.employeeName")}</TableCell> */}
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.type")}</TableCell>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.startDate")}</TableCell>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.endDate")}</TableCell>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.reasonCode")}</TableCell>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.reasonDetails")}</TableCell>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.date")}</TableCell>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.comments")}</TableCell>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.status")}</TableCell>
              <TableCell sx={HeaderStyles}>{t("requestManagement.tableHeaders.action")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {initialLoading ? (
              <TableRow>
                <TableCell colSpan={12} align="center"><CircularProgress size={40} /></TableCell>
              </TableRow>
            ) : filteredRequests.length  > 0 ? (
             filteredRequests.map((row) => {
               // Format dates and times based on request type using actual API field names
               let startDate = "-";
               let endDate = "-";
               let timeText = "-";
               let reasonCode = "-";
               let reasonDetails = "-";

               if (row.type === "LEAVE") {
                 startDate = formatDateOnly(row.leave_start_date);
                 endDate = formatDateOnly(row.leave_end_date);
                 reasonCode = formatReasonCodeLabel(row.leave_reason_code);
                 reasonDetails = row.leave_reason_text || "-";
               } else if (row.type === "SCHEDULE_CHANGE") {
                 startDate = formatDateOnly(row.sched_effective_from);
                 endDate = row.sched_current_days && Array.isArray(row.sched_current_days) 
                   ? row.sched_current_days.join(", ") 
                   : "-";
                 if (row.sched_current_start_time && row.sched_current_end_time) {
                   timeText = `${formatTimeOnly(row.sched_current_start_time)} - ${formatTimeOnly(row.sched_current_end_time)}`;
                 }
                 reasonDetails = row.sched_reason_text || "-";
               } else if (row.type === "GENERIC_HR") {
                 reasonDetails = row.details || "-";
               }

               return (
                <TableRow key={row.id}>
                  <TableCell sx={DataStyles}>{row.id}</TableCell>
                  {/* <TableCell sx={DataStyles}>
                    {row.requester_name || row.requester_full_name || row.employee_name || "-"}
                  </TableCell> */}
                  <TableCell sx={DataStyles}>{formatRequestTypeLabel(row.type)}</TableCell>
                  <TableCell sx={DataStyles}>{startDate}</TableCell>
                  <TableCell sx={DataStyles}>{endDate}</TableCell>
                  <TableCell sx={DataStyles}>{reasonCode}</TableCell>
                  <TableCell sx={DataStyles}>
                    <TruncatedCell text={reasonDetails}/>
                  </TableCell>
                  <TableCell sx={DataStyles}>
                    {formatDateOnly(row.created_at)}
                  </TableCell>
                  <TableCell sx={DataStyles}>
                    <TruncatedCell text={row.admin_comment || "-"}/>
                  </TableCell>
                  <TableCell sx={DataStyles}>
                    <Box sx={{ pointerEvents: "none" }}>
                      <StatusDropdown currentStatus={row.status} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small"   onClick={(e) => {
    setAnchorEl(e.currentTarget);
    setMenuRow(row);   // ✅ store clicked row
  }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={12} align="center">            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 5,
              }}
            >
              <img
                src={emptypayment}
                alt="No Data"
                style={{ width: 200, marginBottom: 16 }}
              />
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#003149",
                }}
              >
                {t("empty.no_user")}
              </CustomText>
            </Box></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => { setAnchorEl(null); setMenuRow(null); }}>
  <MenuItem
  onClick={() => {
    if (menuRow) {
      setSelectedRow(menuRow);   // save the full row
      setDrawerOpen(true);
    }
    setAnchorEl(null);
  }}
  sx={{ fontSize: 13, fontFamily: "Poppins, sans-serif" }}
>
  {t("unvalidatedPunches.tableActions.view")}
</MenuItem>

  {menuRow?.status?.toLowerCase() === "pending" && (
<MenuItem
  onClick={() => {
    setAnchorEl(null);
    setCancelModalOpen(true);
  }}
  sx={{
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    color: "#d32f2f",
  }}
>
  {t("common.cancel") || "Cancel"}
</MenuItem>
  )}

{menuRow?.status?.toLowerCase() === "info_requested" && (
  <MenuItem
    onClick={() => {
      setAnchorEl(null);
      setResubmitModalOpen(true);
    }}
    sx={{
      fontSize: 13,
      fontFamily: "Poppins, sans-serif",
      color: "#006EC2",
    }}
  >
    {t("common.resubmit") || "Resubmit"}
  </MenuItem>
)}

      </Menu>

      {/* Pagination — count from API `pagination.pages` */}
      {totalServerPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalServerPages}
            page={currentPage}
            onChange={(_, val) => setCurrentPage(val)}
            shape="rounded"
            color="primary"
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: "Poppins, sans-serif",
                fontSize: 13,
                fontWeight: 500,
              },
              "& .Mui-selected": {
                backgroundColor: "#E9F3FF",
                color: "#006EC2",
              },
            }}
          />
        </Box>
      )}

 <RequestDetailsDrawer
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  row={selectedRow}
    onStatusChanged={() => setRefreshList((prev) => !prev)}   // 👈 added
/> 

 
<FilterDrawer
  open={filterOpen}
  onClose={() => setFilterOpen(false)}
  filters={filters}
  setFilters={setFilters}
  onApplyFilters={(applied) => {
    setFilters(applied);
    setCurrentPage(1);
  }}
/>
<GenerateRequestDrawer
  open={addDrawerOpen}
  onClose={() => setAddDrawerOpen(false)}
    refreshList={() => setRefreshList((prev) => !prev)}   // 👈 pass refresh callback

/>

<CancelRequestModal
  open={cancelModalOpen}
  loading={cancelLoading}
  onClose={() => setCancelModalOpen(false)}
  onConfirm={() => handleCancelRequest(menuRow?.id)}
/>

<CancelRequestModal
  open={resubmitModalOpen}
  loading={resubmitLoading}
  mode="resubmit"
  token={token}
  requestType={menuRow?.type}   // 👈 auto-detects type from selected row
  onClose={() => setResubmitModalOpen(false)}
  onConfirm={(payload) => handleResubmitRequest(menuRow?.id, payload)}
/>

  </Box>
  </>
  );
};

export default RequestManagement;




